import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getAllSites } from '../../../lib/api';
import { SessionPreflight } from './SessionPreflight';
import { SessionAssetPanel } from './SessionAssetPanel';
import RecordTestModal from '../../../components/RecordTestModal';
import { todayForInput } from '../../../lib/dateUtils';
import { getAssetOptionsForElement } from '../../../constants/AssetTestConfig';
import { useTestSession } from '../../../hooks/useTestSession';
import { useSessionReports } from '../hooks/useSessionReports';
import { useSessionAssets } from '../hooks/useSessionAssets';
import { useSessionResume } from '../hooks/useSessionResume';
import { useSessionTimerSync } from '../hooks/useSessionTimerSync';
import { SessionOptionsMenu } from './SessionOptionsMenu';
import { SessionToolbar } from './SessionToolbar';
import { SessionActionsBar } from './SessionActionsBar';
import { SessionHeader } from './SessionHeader';
import { SessionModals } from './SessionModals';
import { useSessionStore } from '../../../store/sessionStore';
import { useAuthStore } from '../../../store/authStore';
import { validateRcdResult } from '../../../lib/testValidators';
import { isAdmin } from '../../../lib/roles';
import { useMediaQuery, MOBILE_BREAKPOINT } from '../../../hooks/useMediaQuery';
import { getDefaultTestTypeForAssetType } from '../../../lib/sessionGroupingUtils';
import {
  getTimerRemainingMs as computeTimerRemainingMs,
  getTimerStatus as computeTimerStatus,
} from '../../../lib/sessionTimerUtils';
import MobileSessionView from './mobile/MobileSessionView';

// Delay before auto-saving an RCD after a Trip Time keystroke, so a partly-typed
// value (e.g. "2" before "250") is never recorded mid-typing.
const RCD_AUTOSAVE_DEBOUNCE_MS = 900;

export function TestSessionView() {
  // Hooks only at top – no early return before any hook (avoids "Rendered more hooks" crash)
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  // Only admins may cancel/delete a session; technicians leave non-destructively instead.
  const canCancelSession = isAdmin(user);
  const resumeSiteId = location.state?.resumeSiteId;
  const resumeSessionId = location.state?.resumeSessionId;
  const prefillSiteIdFromNav = location.state?.prefillSiteId;

  const {
    sessionId,
    siteId,
    results,
    rcdInputs,
    setRcdInput,
    dualCheckInputs,
    setDualCheckInput,
    initSession,
    registerResult,
    handleInlineRcdSave,
    handleQuickPass,
    isSyncing,
    finishSession,
    isFinishing,
    cancelSession,
    isCancelling,
  } = useTestSession({ resumeSessionId });

  const workOrderNumber = useSessionStore((s) => s.workOrderNumber);
  const setWorkOrderNumber = useSessionStore((s) => s.setWorkOrderNumber);
  const sessionElementType = useSessionStore((s) => s.sessionElementType);
  const setSessionElementType = useSessionStore((s) => s.setSessionElementType);
  const _hasHydrated = useSessionStore((s) => s._hasHydrated);
  const groupTimers = useSessionStore((s) => s.groupTimers);
  const storeStartGroupTimer = useSessionStore((s) => s.startGroupTimer);
  const storeResetGroupTimer = useSessionStore((s) => s.resetGroupTimer);

  const [jobStarted, setJobStarted] = useState(false);
  const [sessionAssetType, setSessionAssetType] = useState('');
  // Bumped after an asset is created (mobile smart form or desktop modal) to
  // force useSessionAssets back to page 1 so a new asset that sorts onto an
  // earlier page than the auto-preload cursor still appears in the list.
  const [assetsRefreshNonce, setAssetsRefreshNonce] = useState(0);
  const sessionTestType = useMemo(
    () => getDefaultTestTypeForAssetType(sessionAssetType),
    [sessionAssetType]
  );

  const {
    emailModal,
    setEmailModal,
    handleExportPdf,
    handleExportDefectReport,
    openEmailModal,
    handleSendEmailReport,
  } = useSessionReports({ sessionId });

  const {
    equipmentCodeSearch,
    setEquipmentCodeSearch,
    debouncedEquipmentCodeSearch,
    sessionSortBy,
    setSessionSortBy,
    sessionSortOrder,
    setSessionSortOrder,
    sessionAssetsPage,
    setSessionAssetsList,
    assetsLoading,
    groupedAssets,
    rawSessionAssets,
    autoPreloadEnabled,
    autoPreloading,
    autoPreloadLoaded,
    autoPreloadTotal,
    autoPreloadPercent,
  } = useSessionAssets({ siteId, jobStarted, sessionAssetType, sessionElementType, refreshNonce: assetsRefreshNonce });

  const [sessionTestDate, setSessionTestDate] = useState(todayForInput());
  const [showFinishSessionModal, setShowFinishSessionModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [modalInitialValues, setModalInitialValues] = useState(null);
  const [, setTick] = useState(0);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  // Per-asset debounce timers for RCD trip-time auto-save (see handleRcdInputChange).
  const rcdAutoSaveTimers = useRef({});

  /* Pre-flight form state (before Start Job); sync from store when store has siteId and job not yet started from this mount */
  const [preFlightSiteId, setPreFlightSiteId] = useState('');
  const [preFlightElementType, setPreFlightElementType] = useState('electrical');
  const [preFlightTestDate, setPreFlightTestDate] = useState(todayForInput());
  const [preFlightWorkOrder, setPreFlightWorkOrder] = useState('');
  const [hydrationTimedOut, setHydrationTimedOut] = useState(false);

  // Drive re-renders for running timers. groupTimers state lives in the store
  // (persisted) so it survives tab switches, navigation, and page refreshes.
  useEffect(() => {
    const active = Object.values(groupTimers).some((t) => t.startedAt != null);
    if (!active) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [groupTimers]);

  useEffect(() => {
    if (jobStarted) return;
    if (prefillSiteIdFromNav != null && String(prefillSiteIdFromNav).trim() !== '') {
      setPreFlightSiteId(String(prefillSiteIdFromNav).trim());
    }
  }, [prefillSiteIdFromNav, jobStarted]);

  useSessionResume({
    resumeSiteId,
    resumeSessionId,
    _hasHydrated,
    sessionId,
    jobStarted,
    sessionElementType,
    isSyncing,
    initSession,
    setJobStarted,
    setSessionElementType,
    registerResult,
  });

  // When sessionElementType changes, reset asset type filter to first option for that element
  const assetOptionsForElement = useMemo(
    () => getAssetOptionsForElement(sessionElementType),
    [sessionElementType]
  );
  useEffect(() => {
    if (jobStarted && sessionElementType && assetOptionsForElement.length > 0) {
      const firstValue = assetOptionsForElement[0]?.value ?? '';
      setSessionAssetType((prev) => {
        const valid = assetOptionsForElement.some((o) => o.value === prev);
        return valid ? prev : firstValue;
      });
    }
  }, [sessionElementType, jobStarted, assetOptionsForElement]);

  const { data: sites, error: _sitesError } = useQuery(
    ['sites'],
    () => getAllSites()
  );

  const isExitEmergency = sessionAssetType === 'combined_exit_emergency';

  const handleRecordModalClose = () => {
    setSelectedAsset(null);
    setModalInitialValues(null);
    queryClient.invalidateQueries(['assets']);
    queryClient.invalidateQueries(['tests']);
  };

  const handleAssetDetailsUpdated = (updated) => {
    if (!updated?.id) return;
    setSessionAssetsList((prev) =>
      prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a))
    );
    setSelectedAsset((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const startGroupTimer = (groupName) => storeStartGroupTimer(groupName);

  // Scope timers to the current site — groupTimers is a flat map with no site
  // key and leaveSession() preserves it, so an untagged read would leak site
  // A's timers into site B's session.
  const scopedTimer = (groupName) => {
    const t = groupTimers[groupName];
    return t && String(t.siteId) === String(siteId) ? t : null;
  };

  const getTimerRemainingMs = (groupName) => computeTimerRemainingMs(scopedTimer(groupName));

  const getTimerStatus = (groupName) => computeTimerStatus(scopedTimer(groupName));

  const getTimerStartedByName = (groupName) => scopedTimer(groupName)?.startedByName ?? null;

  const getTimerStartedAt = (groupName) => scopedTimer(groupName)?.startedAt ?? null;

  // Keep local timers in sync with the server while an Exit/Emergency session is
  // open. Never blocks starting a timer (offline-first) — see useSessionTimerSync.
  useSessionTimerSync({ siteId, enabled: !!(isExitEmergency && sessionId && siteId) });

  const handleStartJob = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!preFlightElementType || String(preFlightElementType).trim() === '') {
      toast.error('Please select an element type');
      return;
    }
    if (!preFlightWorkOrder?.trim()) {
      toast.error('Work order is required');
      return;
    }
    const siteIdToUse = preFlightSiteId?.trim() || siteId;
    if (!siteIdToUse) {
      toast.error('Please select a site');
      return;
    }
    setSessionTestDate(preFlightTestDate);
    setWorkOrderNumber(preFlightWorkOrder);
    setSessionElementType(preFlightElementType);
    initSession(siteIdToUse, null, { sessionType: 'inspection' });
    setJobStarted(true);
    toast.success('Session started — select asset type to begin');
  };

  const handleAssetTypeChange = (e) => {
    setSessionAssetType(e.target.value);
  };

  const handleEquipmentSearchChange = (e) => {
    setEquipmentCodeSearch(e.target.value);
  };

  const openEditModal = (asset, sessionEntry = null) => {
    setModalInitialValues(
      sessionEntry != null && (sessionEntry.status || sessionEntry.testId != null)
        ? { result: sessionEntry.status ?? undefined }
        : null
    );
    setSelectedAsset(asset);
  };

  const openFailEditModal = (asset, overrides = {}) => {
    const testType = overrides.testType ?? (isExitEmergency ? '90-minute Discharge' : sessionTestType);
    setModalInitialValues({
      result: 'fail',
      testType,
      defectsFound: overrides.defectsFound ?? '',
    });
    setSelectedAsset(asset);
  };

  // Auto-save an RCD the moment it is complete, so a tech no longer has to press
  // the bottom "Save" button to persist a test they have already performed:
  //   - Push Button = Fail  -> record immediately (trip time is N/A).
  //   - Push Button = Pass   -> record once a valid Trip Time is present.
  // Trip-time typing is debounced so a half-typed value (e.g. "2" before "250")
  // is never recorded early. This wrapper only ever READS pushButton (it never
  // sets it), and never stages an RCD that already has a result — preserving the
  // "an explicit push result is always required" guard.
  const handleRcdInputChange = useCallback(
    (assetId, patch) => {
      // Preserve the existing store merge behaviour first.
      setRcdInput(assetId, patch);

      if (results[assetId]) return; // already recorded — never stage twice

      const clearTimer = () => {
        if (rcdAutoSaveTimers.current[assetId]) {
          clearTimeout(rcdAutoSaveTimers.current[assetId]);
          delete rcdAutoSaveTimers.current[assetId];
        }
      };
      const readInput = () => useSessionStore.getState().rcdInputs[assetId] ?? {};
      const tripIsValid = (input) => {
        const trip = (input.tripTimeMs ?? '').toString().trim();
        return trip !== '' && validateRcdResult(trip, 'pass').valid;
      };
      // Any selected failure reason (Did Not Trip, Poor Condition, Poor
      // Fitment, Other) forces the result to Fail regardless of trip time —
      // mirrors handleInlineRcdSave / buildRecordTestPayload.
      const hasReason = (input) => !!(input.reason && String(input.reason).trim());

      // A push-button toggle is a deliberate, explicit action — evaluate now.
      if (Object.prototype.hasOwnProperty.call(patch, 'pushButton')) {
        clearTimer();
        const input = readInput();
        if (input.pushButton === 'fail') {
          handleInlineRcdSave({ id: assetId }, sessionTestDate);
        } else if (input.pushButton === 'pass' && (hasReason(input) || tripIsValid(input))) {
          handleInlineRcdSave({ id: assetId }, sessionTestDate);
        }
        return;
      }

      // A failure-reason selection is a deliberate action — evaluate now, but
      // still require an explicit push-button result first (unset stays unset).
      if (Object.prototype.hasOwnProperty.call(patch, 'reason')) {
        clearTimer();
        const input = readInput();
        if (hasReason(input) && (input.pushButton === 'pass' || input.pushButton === 'fail')) {
          handleInlineRcdSave({ id: assetId }, sessionTestDate);
        }
        return;
      }

      // A trip-time keystroke while push = pass — debounce, then re-check + save.
      if (Object.prototype.hasOwnProperty.call(patch, 'tripTimeMs')) {
        clearTimer();
        const input = readInput();
        if (input.pushButton !== 'pass' || !tripIsValid(input)) return;
        rcdAutoSaveTimers.current[assetId] = setTimeout(() => {
          delete rcdAutoSaveTimers.current[assetId];
          const latest = useSessionStore.getState();
          if (latest.results[assetId]) return;
          const cur = latest.rcdInputs[assetId] ?? {};
          if (cur.pushButton === 'pass' && tripIsValid(cur)) {
            handleInlineRcdSave({ id: assetId }, sessionTestDate);
          }
        }, RCD_AUTOSAVE_DEBOUNCE_MS);
      }
    },
    [results, sessionTestDate, setRcdInput, handleInlineRcdSave]
  );

  // Clear any pending RCD auto-save timers on unmount to avoid firing after the
  // session view is gone.
  useEffect(() => {
    const timers = rcdAutoSaveTimers.current;
    return () => {
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  const isSessionActive = !!(sessionId && siteId && sessionAssetType);
  /** Session is underway (site + server identity) — used for Cancel/Finish even before asset type pick */
  const sessionStarted = !!(sessionId && siteId && jobStarted);
  const currentSite = sites?.find((s) => String(s.id) === String(siteId));
  const siteDisplayName = currentSite?.display_name ?? currentSite?.name ?? currentSite?.site_code ?? 'Site';

  // Mobile back: go to Site Details when we have a site (from session or from prefill), else Sites list
  const backTargetSiteId = siteId || preFlightSiteId?.trim() || (prefillSiteIdFromNav != null && String(prefillSiteIdFromNav).trim() !== '' ? String(prefillSiteIdFromNav).trim() : '');
  const backTargetSite = sites?.find((s) => String(s.id) === String(backTargetSiteId));
  const backTargetDisplayName = backTargetSite?.display_name ?? backTargetSite?.name ?? backTargetSite?.site_code ?? 'Site';

  // Fallback: if hydration takes >2s, show form anyway (fixes stuck "Loading session…").
  useEffect(() => {
    if (_hasHydrated) return;
    const t = setTimeout(() => setHydrationTimedOut(true), 2000);
    return () => clearTimeout(t);
  }, [_hasHydrated]);

  // Wait for persisted store state to load before rendering anything.
  if (!_hasHydrated && !hydrationTimedOut) {
    return <div className="test-session-content"><p className="text-muted">Loading session…</p></div>;
  }

  // Mobile: hand off to dedicated session view once the session is active.
  // Pre-flight (before jobStarted) keeps the existing SessionPreflight on mobile.
  if (isMobile && sessionStarted) {
    return (
      <MobileSessionView
        siteName={siteDisplayName}
        siteId={siteId}
        sessionId={sessionId}
        elementType={sessionElementType}
        onBack={() => navigate(backTargetSiteId ? `/sites/${backTargetSiteId}` : '/sites')}
        sessionAssetType={sessionAssetType}
        assetOptionsForElement={assetOptionsForElement}
        onAssetTypeChange={handleAssetTypeChange}
        equipmentCodeSearch={equipmentCodeSearch}
        onEquipmentSearchChange={handleEquipmentSearchChange}
        sessionSortBy={sessionSortBy}
        setSessionSortBy={setSessionSortBy}
        sessionSortOrder={sessionSortOrder}
        setSessionSortOrder={setSessionSortOrder}
        sessionTestDate={sessionTestDate}
        sessionTestType={sessionTestType}
        isExitEmergency={isExitEmergency}
        isSyncing={isSyncing}
        groupedAssets={groupedAssets}
        assetsLoading={assetsLoading}
        results={results}
        rcdInputs={rcdInputs}
        setRcdInput={handleRcdInputChange}
        dualCheckInputs={dualCheckInputs}
        setDualCheckInput={setDualCheckInput}
        handleInlineRcdSave={handleInlineRcdSave}
        handleQuickPass={handleQuickPass}
        registerResult={registerResult}
        onAssetDetailsUpdated={handleAssetDetailsUpdated}
        getTimerStatus={getTimerStatus}
        getTimerRemainingMs={getTimerRemainingMs}
        startGroupTimer={startGroupTimer}
        resetGroupTimer={storeResetGroupTimer}
        onFinish={finishSession}
        isFinishing={isFinishing}
        onCancel={cancelSession}
        canCancelSession={canCancelSession}
        onExportFullReport={handleExportPdf}
        onExportDefectReport={handleExportDefectReport}
        onSendEmailReport={handleSendEmailReport}
        siteEmail={currentSite?.contact_email ?? ''}
        onAssetCreated={() => setAssetsRefreshNonce((n) => n + 1)}
      />
    );
  }

  const sessionMobileTitle = siteId ? siteDisplayName : jobStarted ? 'Select site' : backTargetSiteId ? backTargetDisplayName : 'Start session';
  const backToLabel = backTargetSiteId ? `Back to ${backTargetDisplayName}` : 'Back to sites';
  const optionsMenuBlock = (
    <SessionOptionsMenu
      visible={jobStarted && !!siteId}
      sessionSortBy={sessionSortBy}
      setSessionSortBy={setSessionSortBy}
      sessionSortOrder={sessionSortOrder}
      setSessionSortOrder={setSessionSortOrder}
      onAddAsset={() => setShowAddAssetModal(true)}
      onExportPdf={handleExportPdf}
      onExportDefectReport={handleExportDefectReport}
      onCancelSession={() => setShowCancelConfirm(true)}
      canCancelSession={canCancelSession}
      sessionId={sessionId}
      isSyncing={isSyncing}
    />
  );

  return (
    <>
      <SessionHeader
        isMobile={isMobile}
        backToLabel={backToLabel}
        sessionMobileTitle={sessionMobileTitle}
        siteId={siteId}
        siteDisplayName={siteDisplayName}
        jobStarted={jobStarted}
        optionsMenu={optionsMenuBlock}
        onBack={() => navigate(backTargetSiteId ? `/sites/${backTargetSiteId}` : '/sites')}
      />

      <div className={isMobile ? `session-mobile-content${!jobStarted ? ' session-mobile-content-preflight' : ''}` : ''}>
      {/* Pre-flight: Start Session form when job not started (site from context when navigated from Site Details) */}
      {!jobStarted && (
        <SessionPreflight
          workOrder={preFlightWorkOrder}
          onWorkOrderChange={setPreFlightWorkOrder}
          elementType={preFlightElementType}
          onElementTypeChange={setPreFlightElementType}
          preFlightSiteId={preFlightSiteId}
          onSiteChange={setPreFlightSiteId}
          siteId={siteId}
          sites={sites}
          testDate={preFlightTestDate}
          onTestDateChange={setPreFlightTestDate}
          onSubmit={handleStartJob}
        />
      )}

      {sessionStarted && (
        <SessionActionsBar
          isSessionActive={isSessionActive}
          inspectedCount={rawSessionAssets.filter((a) => results[a.id]).length}
          totalCount={autoPreloadTotal}
          isSyncing={isSyncing}
          onFinish={() => setShowFinishSessionModal(true)}
        />
      )}

      {/* Session toolbar: one row — asset type + search (options menu is beside breadcrumb) */}
      {jobStarted && siteId && (
        <SessionToolbar
          sessionAssetType={sessionAssetType}
          assetOptionsForElement={assetOptionsForElement}
          onAssetTypeChange={handleAssetTypeChange}
          equipmentCodeSearch={equipmentCodeSearch}
          onEquipmentSearchChange={handleEquipmentSearchChange}
          isMobile={isMobile}
          workOrderNumber={workOrderNumber}
        />
      )}

      {jobStarted && (
        <SessionAssetPanel
          siteId={siteId}
          sessionAssetType={sessionAssetType}
          isExitEmergency={isExitEmergency}
          assetsLoading={assetsLoading}
          sessionAssetsPage={sessionAssetsPage}
          debouncedEquipmentCodeSearch={debouncedEquipmentCodeSearch}
          groupedAssets={groupedAssets}
          autoPreloadEnabled={autoPreloadEnabled}
          autoPreloading={autoPreloading}
          autoPreloadLoaded={autoPreloadLoaded}
          autoPreloadTotal={autoPreloadTotal}
          autoPreloadPercent={autoPreloadPercent}
          getTimerStatus={getTimerStatus}
          getTimerRemainingMs={getTimerRemainingMs}
          getTimerStartedByName={getTimerStartedByName}
          getTimerStartedAt={getTimerStartedAt}
          startGroupTimer={startGroupTimer}
          resetGroupTimer={storeResetGroupTimer}
          assetListProps={{
            results,
            sessionTestType,
            sessionTestDate,
            isSyncing,
            rcdInputs,
            setRcdInput: handleRcdInputChange,
            dualCheckInputs,
            setDualCheckInput,
            handleInlineRcdSave,
            handleQuickPass,
            openEditModal,
            openFailEditModal,
          }}
        />
      )}

      {selectedAsset && (
        <RecordTestModal
          key={`${selectedAsset.id}-${results[selectedAsset.id]?.testId ?? 'new'}-${modalInitialValues?.result ?? 'edit'}`}
          asset={selectedAsset}
          onClose={handleRecordModalClose}
          onRecordSuccess={(assetId, { result, testId, tripTimeMs }) => registerResult(assetId, { result, testId, tripTimeMs })}
          existingTestId={results[selectedAsset.id]?.testId ?? null}
          initialResult={modalInitialValues?.result}
          initialTestType={modalInitialValues?.testType}
          initialDefectsFound={modalInitialValues?.defectsFound}
          initialTripTimeMs={selectedAsset ? (rcdInputs[selectedAsset.id]?.tripTimeMs ?? '') : ''}
          initialPushButton={selectedAsset ? (rcdInputs[selectedAsset.id]?.pushButton ?? null) : null}
          sessionId={sessionId ?? null}
          onAssetDetailsUpdated={handleAssetDetailsUpdated}
        />
      )}

      </div>

      <SessionModals
        showCancelConfirm={showCancelConfirm}
        isCancelling={isCancelling}
        onCloseCancel={() => setShowCancelConfirm(false)}
        onConfirmCancel={async () => {
          try {
            await cancelSession();
            setJobStarted(false);
            setShowCancelConfirm(false);
            navigate('/');
          } catch (err) {
            toast.error('Failed to cancel session. Please try again.');
          }
        }}
        showFinishSessionModal={showFinishSessionModal}
        sessionResults={results}
        sessionAssets={groupedAssets.flatMap((g) => g.items)}
        onCloseFinish={() => setShowFinishSessionModal(false)}
        onCompleteFinish={async () => {
          try {
            await finishSession();
            setJobStarted(false);
            setShowFinishSessionModal(false);
            navigate('/');
          } catch (err) {
            toast.error('Failed to end session');
          }
        }}
        isFinishing={isFinishing}
        onExportPdf={handleExportPdf}
        onExportDefectReport={handleExportDefectReport}
        onEmailSessionReport={sessionId ? () => openEmailModal('session') : undefined}
        onEmailDefectReport={sessionId ? () => openEmailModal('defect') : undefined}
        canExport={!!sessionId}
        isSyncing={isSyncing}
        emailModalOpen={emailModal.open}
        emailReportType={emailModal.reportType}
        onCloseEmail={() => setEmailModal({ open: false, reportType: 'session' })}
        defaultRecipient={user?.email || ''}
        onSendEmail={handleSendEmailReport}
        showAddAssetModal={showAddAssetModal}
        siteId={siteId}
        siteDisplayName={siteDisplayName}
        elementType={sessionElementType || 'electrical'}
        sessionAssetType={sessionAssetType}
        onAddAssetSuccess={() => { setShowAddAssetModal(false); setAssetsRefreshNonce((n) => n + 1); }}
        onCloseAddAsset={() => setShowAddAssetModal(false)}
      />
    </>
  );
}
